import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Validators, FormControl, FormBuilder, FormGroup } from '@angular/forms';
import { CustomValidator } from '../../_common/customvalidators';
import { Router, NavigationExtras, NavigationEnd } from '@angular/router';
import { CommonService } from '../../_services/common.service';
import { URLS } from 'src/app/_config/api.config';
import { ApiService } from 'src/app/_services/api-data.service';
import { ToastService } from '../../_services/toast.service';
import { filter } from 'rxjs/operators';
import { ReCaptcha2Component } from 'ngx-captcha';         
import { environment } from 'src/environments/environment';   

@Component({
	selector: 'app-forgot-password',
	templateUrl: './forgot-password.component.html',
	styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnInit {
	@ViewChild('emailInput', { static: false }) emailInput!: ElementRef<HTMLInputElement>;
	@ViewChild('captchaElem') captchaElement!: ReCaptcha2Component;  

	// reCAPTCHA
	readonly recaptchaSiteKey = environment.recaptchaSiteKey;         
	readonly captchaEnabled = environment.enableCaptcha;             
	isCaptchaValid: boolean = !environment.enableCaptcha;             
	captchaResponse: string = '';                                     

	public subscription = new Subscription();
	isLoading = false;
	public desktopView: boolean = false;
	message: string = '';
	approvalText: string = '';

	public validation_messages = {
		email: [
			{ type: 'required', message: 'ForgotPasswordComponent.emailRequired' },
			{ type: 'invalidEmailPhone', message: 'ForgotPasswordComponent.invalidEmail' }
		]
	}

	forgotPassFromGroup: FormGroup;

	constructor(
		private platform: Platform,
		private formBuilder: FormBuilder,
		private router: Router,
		public apiService: ApiService,
		public commonService: CommonService,
		private dataService: ApiService,
		private toastService: ToastService,
	) {
		this.desktopView = this.platform.is('desktop');
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd)
		).subscribe((route: NavigationEnd) => {
			if (route.url == '/forgot-password') {
				setTimeout(() => {
					if (this.emailInput) {
						this.emailInput.nativeElement.focus();
					}
				}, 600);
			}
		});
	}

	ngOnInit() {
		this.forgotPassFromGroup = this.initializeforgotPassFromGroup();
		this.resetCaptchaState(); 
		setTimeout(() => {
			if (this.emailInput) {
				this.emailInput.nativeElement.focus();
			}
		}, 600);
	}

	ionViewWillEnter() {        
		this.resetCaptchaState();
		this.resetCaptcha();
	}

	send() {
		for (const i in this.forgotPassFromGroup.controls) {
			this.forgotPassFromGroup.controls[i].markAsDirty();
			this.forgotPassFromGroup.controls[i].updateValueAndValidity();
			this.forgotPassFromGroup.controls[i].markAsTouched();
		}
		if (this.forgotPassFromGroup.invalid) { return; }
		if (!this.isCaptchaValid) { return; }   

		this.generateOtp();
		this.resetCaptchaState();
	}

	generateOtp() {
		const params = {
			'user_name': this.forgotPassFromGroup.controls.email.value,
			'purpose_type': 'FORGOT_PASSWORD',
			...(this.captchaEnabled ? { captcha_token: this.captchaResponse } : {})  
		}

		const navigationExtras: NavigationExtras = {
			queryParams: { from: 'forgot_password' },
		};

		this.isLoading = true;
		const otpUrl = this.captchaEnabled ? URLS.generateOtpSecure : URLS.generateOtp;
		this.subscription.add(
			this.dataService.post(otpUrl, params).subscribe(data => {
				if (data['code'] == 200) {
					if (this.forgotPassFromGroup.controls.email.value) {
						localStorage.setItem('user_name', this.forgotPassFromGroup.controls.email.value);
					}
					this.forgotPassFromGroup.reset();
					this.resetCaptchaState();  
					this.router.navigate(['/two-factor-authentication'], navigationExtras);
					if (data['data']) {
						if (data['data']['uuid']) {
							localStorage.setItem('uuid', data['data']['uuid']);
						}
						if (data['data']['mobile_no']) {
							localStorage.setItem('mobile_no', data['data']['mobile_no']);
						}
					}
				} else {
					this.toastService.displayToast(data['message'], null, 'danger');
					this.resetCaptcha();  
				}
				this.isLoading = false;
			}, error => {
				this.isLoading = false;
				this.toastService.displayToast(error['message'], null, 'danger');
				this.resetCaptcha();      
			})
		);
	}

	goToPage(page) {
		this.router.navigate([page]);
	}

	initializeforgotPassFromGroup() {
		return this.formBuilder.group({
			email: new FormControl(null, {
				validators: Validators.compose([Validators.required, CustomValidator.emailPhoneValidator]),
			}),
			//Only include recaptcha control if captcha is enabled
			...(this.captchaEnabled ? {
				recaptcha: new FormControl('', Validators.required)
			} : {})
		});
	}

	// Captcha methods

	private resetCaptchaState(): void {
		if (this.captchaEnabled) {
			this.isCaptchaValid = false;
			this.captchaResponse = '';
			const control = this.forgotPassFromGroup?.get('recaptcha');
			if (control) {
				control.setValue('');
				control.markAsUntouched();
				control.markAsPristine();
			}
		} else {
			this.isCaptchaValid = true;
			this.captchaResponse = '';
		}
	}

	resetCaptcha(): void {
		if (!this.captchaEnabled) return;
		this.resetCaptchaState();
		if (this.captchaElement) {
			try {
				this.captchaElement.resetCaptcha();
			} catch (e) {
				console.log('Captcha not ready for reset');
			}
		}
	}

	handleCaptchaSuccess(captchaResponse: string): void {
		if (!this.captchaEnabled) return;
		this.isCaptchaValid = true;
		this.captchaResponse = captchaResponse;
	}

	handleCaptchaExpire(): void {
		if (!this.captchaEnabled) return;
		this.isCaptchaValid = false;
		this.captchaResponse = '';
		this.forgotPassFromGroup.get('recaptcha')?.setValue('');
	}

	handleCaptchaError(): void {
		if (!this.captchaEnabled) return;
		this.isCaptchaValid = false;
		this.captchaResponse = '';
	}

	ionViewWillLeave() {
		this.subscription.unsubscribe();
	}
}